import * as React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { tokens } from '../DashboardTheme/DashboardTheme';

/* ------------------------------------------------------------------
   Shared type scale.
   Display + headings use the condensed fight-poster stack; body copy
   uses Inter. All headings are uppercase with tight tracking.
------------------------------------------------------------------ */

export function RegoularH1({ children, color = tokens.white, textAlign = 'center', marginLeft, fontSize = 50, marginBottom = 5, paddingTop = 3, hoverColor = null }) {
    return (
        <Typography
            variant="h1"
            sx={{
                textAlign,
                fontFamily: tokens.heading,
                fontWeight: 700,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                fontSize,
                marginLeft,
                color,
                marginBottom,
                paddingTop,
                transition: `color 0.3s ${tokens.ease}`,
                ...(hoverColor && {
                    '&:hover': { cursor: 'pointer', color: hoverColor }
                })
            }}
        >
            {children}
        </Typography>
    );
}

export function RegoularH2({ children, color = tokens.white, fontSize = 30, marginLeft, paddingBottom, marginRight, marginBottom = 2, borderBottom, borderBottomHover, paddingTop = 2, paddingRight, textAlign = 'center', hoverColor = null, transition }) {
    return (
        <Typography
            variant="h2"
            sx={{
                paddingRight,
                marginLeft,
                marginRight,
                textAlign,
                fontFamily: tokens.heading,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                lineHeight: 1.15,
                fontSize,
                color,
                marginBottom,
                paddingBottom,
                paddingTop,
                transition: transition || `color 0.3s ${tokens.ease}`,
                borderBottom,
                ...(hoverColor && {
                    '&:hover': { cursor: 'pointer', color: hoverColor, borderBottom: borderBottomHover }
                })
            }}
        >
            {children}
        </Typography>
    );
}

export default function RegoularH1HomePage({ children, position = 'absolute', color = tokens.white, fontSize = 30, marginBottom = 2, paddingTop = 20, hoverColor = null }) {
    return (
        <Typography
            variant="h1"
            sx={{
                zIndex: 1,
                position,
                fontFamily: tokens.display,
                fontSize,
                color,
                marginBottom,
                paddingTop,
                left: '29%',
                textTransform: 'uppercase',
                transition: `color 0.3s ${tokens.ease}`,
                ...(hoverColor && {
                    '&:hover': { cursor: 'pointer', color: hoverColor }
                })
            }}
        >
            {children}
        </Typography>
    );
}

export function TeamVivenzioTitle({ children, color = tokens.white, fontSize = 50, marginTop, marginLeft = null, marginBottom = 0, paddingTop = 2, paddingRight, textAlign = 'center', hoverColor = null, letterSpacing = '0.03em', lineHeight = 1 }) {
    return (
        <Typography
            variant="h2"
            sx={{
                marginTop,
                paddingRight,
                marginLeft,
                textAlign,
                fontFamily: tokens.display,
                fontSize,
                color,
                marginBottom,
                paddingTop,
                letterSpacing,
                lineHeight,
                transition: `color 0.3s ${tokens.ease}`,
                ...(hoverColor && {
                    '&:hover': { cursor: 'pointer', color: hoverColor }
                })
            }}
        >
            {children}
        </Typography>
    );
}

/* --- New: small uppercase eyebrow label with a leading rule --- */
export function Kicker({ children, color = tokens.red, justifyContent = 'center', sx }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent, gap: 1.5, ...sx }}>
            <Box sx={{ width: 28, height: '2px', backgroundColor: color }} />
            <Typography
                component="span"
                sx={{
                    fontFamily: tokens.heading,
                    fontWeight: 600,
                    fontSize: { xs: 12, md: 14 },
                    letterSpacing: '0.32em',
                    textTransform: 'uppercase',
                    color,
                }}
            >
                {children}
            </Typography>
        </Box>
    );
}

/* --- New: body copy with sensible measure and muted tone --- */
export function BodyText({ children, color = tokens.muted, maxWidth = 620, textAlign = 'center', fontSize = { xs: 15, md: 17 }, sx }) {
    return (
        <Typography
            sx={{
                fontFamily: tokens.body,
                fontWeight: 300,
                fontSize,
                lineHeight: 1.75,
                color,
                maxWidth,
                textAlign,
                mx: textAlign === 'center' ? 'auto' : 0,
                ...sx,
            }}
        >
            {children}
        </Typography>
    );
}
